import { useEffect, useRef, useState } from 'react'

import { Language } from '../../../../../common/types/i18n.js'
import { showToast } from '../../../../toast.js'
import {
    addHoliday,
    createCustomField,
    createLeavePolicyBundle,
    deleteCustomField,
    deleteHoliday,
    deleteLeavePolicyBundle,
    fetchBundleEmployees,
    fetchHolidays,
    fetchLeavePolicies,
    fetchLeavePolicyBundles,
    fetchSettingsCustomFields,
    fetchSettingsGeneral,
    saveBundleEmployees,
    updateCustomField,
    updateHoliday,
    updateLeavePolicyBundle,
} from '../../utils/portal-api.js'
import type {
    LeavePolicyBundle,
    PortalPermissions,
    SettingsCompany,
    SettingsCustomField,
    SettingsHoliday,
    SettingsPolicy,
} from '../../utils/portal-api.js'
import type { PickerEmployee } from './sections/policy-employee-picker.js'
import type { SettingsTab } from './settings-view.js'
import { SettingsView } from './settings-view.js'

export interface SettingsViewWiredProps {
    lang: Language
    permissions: PortalPermissions
    activeTab: SettingsTab
}

export const SettingsViewWired = ({ lang, permissions, activeTab }: SettingsViewWiredProps) => {
    const [loading, setLoading] = useState(true)
    const [companies, setCompanies] = useState<SettingsCompany[]>([])
    // Policies feed the bundle editor's type picker only; editing a policy's
    // rules moved to the self-contained LeaveTypesSection.
    const [policies, setPolicies] = useState<SettingsPolicy[]>([])

    // ── Leave policy bundles (puhkusepoliitikad) ──
    const [bundles, setBundles] = useState<LeavePolicyBundle[]>([])
    const [bundleSlideOpen, setBundleSlideOpen] = useState(false)
    const [editingBundle, setEditingBundle] = useState<LeavePolicyBundle | null>(null)
    const [bundleForm, setBundleForm] = useState<{ name: string; policyIds: number[] }>({
        name: '',
        policyIds: [],
    })
    const [bundleSaving, setBundleSaving] = useState(false)
    const [bundleError, setBundleError] = useState<string | null>(null)
    const [bundleEmployeeIds, setBundleEmployeeIds] = useState<number[]>([])
    const [bundleSelectedEmployees, setBundleSelectedEmployees] = useState<PickerEmployee[]>([])
    // True once the edited bundle's members have loaded — guards the member PUT
    // so a failed load can't silently wipe members on save.
    const [bundleMembersLoaded, setBundleMembersLoaded] = useState(false)
    // The bundle id whose member fetch is current; stale responses are dropped.
    const editingBundleIdRef = useRef<number | null>(null)

    const [holidays, setHolidays] = useState<SettingsHoliday[]>([])
    const [holidayYear, setHolidayYear] = useState(new Date().getFullYear())
    const [holidayCompanyId, setHolidayCompanyId] = useState(1)

    const [customFields, setCustomFields] = useState<SettingsCustomField[]>([])
    const [fieldSlideOpen, setFieldSlideOpen] = useState(false)
    const [editingField, setEditingField] = useState<SettingsCustomField | null>(null)
    const [fieldForm, setFieldForm] = useState({
        name: '',
        field_type: 'text',
        options: '',
        visibility: 'public',
    })
    const [savingField, setSavingField] = useState(false)

    // ── Loaders ──

    const loadGeneral = async () => {
        const result = await fetchSettingsGeneral()
        if (result.ok) setCompanies(result.data.companies)
    }

    const loadPolicies = async () => {
        const [polRes, bundleRes] = await Promise.all([
            fetchLeavePolicies(),
            fetchLeavePolicyBundles(),
        ])
        if (polRes.ok) setPolicies(polRes.data)
        if (bundleRes.ok) setBundles(bundleRes.data)
    }

    const loadHolidays = async (year: number, companyId?: number) => {
        const cid = companyId ?? holidayCompanyId
        const result = await fetchHolidays(year, cid)
        if (result.ok) setHolidays(result.data)
    }

    const loadCustomFields = async () => {
        const result = await fetchSettingsCustomFields()
        if (result.ok) setCustomFields(result.data)
    }

    // ── Effects ──

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        void (async () => {
            await loadGeneral()
            if (activeTab === 'leavePolicies') await loadPolicies()
            else if (activeTab === 'holidays') await loadHolidays(holidayYear)
            else if (activeTab === 'customFields') await loadCustomFields()
            if (!cancelled) setLoading(false)
        })()
        return () => {
            cancelled = true
        }
    }, [activeTab])

    // ── Bundle handlers ──

    const resetBundleForm = () => {
        editingBundleIdRef.current = null
        setEditingBundle(null)
        setBundleForm({ name: '', policyIds: [] })
        setBundleEmployeeIds([])
        setBundleSelectedEmployees([])
        setBundleMembersLoaded(false)
        setBundleError(null)
    }

    const handleOpenBundleCreate = () => {
        resetBundleForm()
        setBundleSlideOpen(true)
    }

    const handleOpenBundleEdit = (bundle: LeavePolicyBundle) => {
        const bundleId = bundle.id
        editingBundleIdRef.current = bundleId
        setEditingBundle(bundle)
        setBundleForm({
            name: bundle.name,
            policyIds: bundle.items.map((i) => i.policy_id),
        })
        setBundleError(null)
        setBundleEmployeeIds([])
        setBundleSelectedEmployees([])
        setBundleMembersLoaded(false)
        void (async () => {
            const result = await fetchBundleEmployees(bundleId)
            // Drop stale responses — the editor may have moved to another bundle.
            if (editingBundleIdRef.current !== bundleId) return
            if (result.ok) {
                setBundleEmployeeIds(result.data.map((m) => m.id))
                setBundleSelectedEmployees(result.data)
                setBundleMembersLoaded(true)
            } else {
                setBundleError(result.error)
            }
        })()
        setBundleSlideOpen(true)
    }

    const handleCloseBundleSlide = () => {
        setBundleSlideOpen(false)
        resetBundleForm()
    }

    const handleSaveBundle = async () => {
        setBundleSaving(true)
        setBundleError(null)
        try {
            let bundleId: number | undefined

            if (editingBundle) {
                const result = await updateLeavePolicyBundle(editingBundle.id, {
                    name: bundleForm.name,
                    policy_ids: bundleForm.policyIds,
                })
                if (!result.ok) {
                    setBundleError(result.error)
                    return
                }
                bundleId = editingBundle.id
                // Only PUT members when they actually loaded — otherwise a
                // failed fetch would wipe all manual members on a routine save.
                if (bundleMembersLoaded) {
                    const memberResult = await saveBundleEmployees(bundleId, bundleEmployeeIds)
                    if (!memberResult.ok) {
                        setBundleError(memberResult.error)
                        return
                    }
                }
            } else {
                const result = await createLeavePolicyBundle({
                    name: bundleForm.name,
                    policy_ids: bundleForm.policyIds,
                })
                if (!result.ok) {
                    setBundleError(result.error)
                    return
                }
                bundleId = result.data.id
                if (bundleEmployeeIds.length > 0) {
                    const memberResult = await saveBundleEmployees(bundleId, bundleEmployeeIds)
                    if (!memberResult.ok) {
                        setBundleError(memberResult.error)
                        return
                    }
                }
            }

            setBundleSlideOpen(false)
            resetBundleForm()
            await loadPolicies()
        } finally {
            setBundleSaving(false)
        }
    }

    const handleDeleteBundle = async (id: number) => {
        if (bundleSaving) return
        setBundleSaving(true)
        try {
            const result = await deleteLeavePolicyBundle(id)
            if (!result.ok && result.error) {
                setBundleError(result.error)
                showToast(result.error, 'error')
                return
            }
            setBundleSlideOpen(false)
            resetBundleForm()
            await loadPolicies()
        } finally {
            setBundleSaving(false)
        }
    }

    const handleAddHoliday = async (date: string, name: string, companyId: number) => {
        const result = await addHoliday({ date, name, company_id: companyId })
        if (!result.ok && result.error) {
            showToast(result.error, 'error')
            return
        }
        await loadHolidays(holidayYear)
    }

    const handleUpdateHoliday = async (id: number, date: string, name: string) => {
        const result = await updateHoliday(id, { date, name })
        if (!result.ok && result.error) {
            showToast(result.error, 'error')
            return
        }
        await loadHolidays(holidayYear)
    }

    const handleDeleteHoliday = async (id: number) => {
        await deleteHoliday(id)
        await loadHolidays(holidayYear)
    }

    const handleChangeHolidayYear = (year: number) => {
        setHolidayYear(year)
        void loadHolidays(year)
    }

    const handleChangeHolidayCompany = (companyId: number) => {
        setHolidayCompanyId(companyId)
        void loadHolidays(holidayYear, companyId)
    }

    const handleSaveField = async () => {
        setSavingField(true)
        try {
            if (editingField) {
                await updateCustomField(editingField.id, fieldForm)
            } else {
                await createCustomField(fieldForm)
            }
            setFieldSlideOpen(false)
            setEditingField(null)
            await loadCustomFields()
        } finally {
            setSavingField(false)
        }
    }

    const handleDeleteField = async (id: number) => {
        await deleteCustomField(id)
        await loadCustomFields()
    }

    return (
        <SettingsView
            lang={lang}
            permissions={permissions}
            activeTab={activeTab}
            loading={loading}
            companies={companies}
            policies={policies}
            bundles={bundles}
            bundleSlideOpen={bundleSlideOpen}
            editingBundle={editingBundle}
            bundleForm={bundleForm}
            bundleSaving={bundleSaving}
            bundleError={bundleError}
            onOpenBundleCreate={handleOpenBundleCreate}
            onOpenBundleEdit={handleOpenBundleEdit}
            onCloseBundleSlide={handleCloseBundleSlide}
            onUpdateBundleName={(value) => setBundleForm((prev) => ({ ...prev, name: value }))}
            onToggleBundlePolicy={(policyId) =>
                setBundleForm((prev) => ({
                    ...prev,
                    policyIds: prev.policyIds.includes(policyId)
                        ? prev.policyIds.filter((id) => id !== policyId)
                        : [...prev.policyIds, policyId],
                }))
            }
            onSaveBundle={() => void handleSaveBundle()}
            onDeleteBundle={(id) => void handleDeleteBundle(id)}
            bundleEmployeeIds={bundleEmployeeIds}
            bundleSelectedEmployees={bundleSelectedEmployees}
            onAddBundleEmployee={(emp) => {
                setBundleEmployeeIds((prev) => (prev.includes(emp.id) ? prev : [...prev, emp.id]))
                setBundleSelectedEmployees((prev) =>
                    prev.some((e) => e.id === emp.id) ? prev : [...prev, emp],
                )
            }}
            onRemoveBundleEmployee={(id) => {
                setBundleEmployeeIds((prev) => prev.filter((eid) => eid !== id))
                setBundleSelectedEmployees((prev) => prev.filter((e) => e.id !== id))
            }}
            onSelectAllBundleEmployees={(employees) => {
                setBundleEmployeeIds((prev) => {
                    const set = new Set(prev)
                    for (const e of employees) set.add(e.id)
                    return [...set]
                })
                setBundleSelectedEmployees((prev) => {
                    const existing = new Set(prev.map((e) => e.id))
                    return [...prev, ...employees.filter((e) => !existing.has(e.id))]
                })
            }}
            onClearAllBundleEmployees={() => {
                setBundleEmployeeIds([])
                setBundleSelectedEmployees([])
            }}
            holidays={holidays}
            holidayYear={holidayYear}
            holidayCompanyId={holidayCompanyId}
            onChangeHolidayYear={handleChangeHolidayYear}
            onChangeHolidayCompany={handleChangeHolidayCompany}
            onAddHoliday={(d, n, cid) => void handleAddHoliday(d, n, cid)}
            onUpdateHoliday={(id, d, n) => void handleUpdateHoliday(id, d, n)}
            onDeleteHoliday={(id) => void handleDeleteHoliday(id)}
            customFields={customFields}
            fieldSlideOpen={fieldSlideOpen}
            editingField={editingField}
            fieldForm={fieldForm}
            savingField={savingField}
            onOpenFieldCreate={() => {
                setEditingField(null)
                setFieldForm({ name: '', field_type: 'text', options: '', visibility: 'public' })
                setFieldSlideOpen(true)
            }}
            onOpenFieldEdit={(f) => {
                setEditingField(f)
                setFieldForm({
                    name: f.name,
                    field_type: f.field_type,
                    options: f.options ?? '',
                    visibility: f.visibility,
                })
                setFieldSlideOpen(true)
            }}
            onCloseFieldSlide={() => {
                setFieldSlideOpen(false)
                setEditingField(null)
            }}
            onUpdateFieldForm={(field, value) =>
                setFieldForm((prev) => ({ ...prev, [field]: value }))
            }
            onSaveField={() => void handleSaveField()}
            onDeleteField={(id) => void handleDeleteField(id)}
            showToast={showToast}
        />
    )
}
